import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Api, getToken, ImageApi } from "../../../api";
import {
  ArtData,
  ArtKeyComponents,
  CURRENCY_TYPE,
  SITE_DATA_PARTITIONS,
  MEDIUM_TYPE,
} from "../../../../../types";
import { ImagePreview } from "../../../components/ImagePreview";
import { Input } from "../../../components/forms";
import { isSameFile } from "../../../utils/image";
import { Layout } from "../../../components/Layout";
import { Button, BUTTON_COLORS, BUTTON_TYPE } from "../../../components/Button";
import { useModal } from "../../../providers/modal";
import { usePermissions } from "../../../providers/authProvider";
import { Loader } from "../../../components/Loader";
import { useOrder } from "../../../hooks/useOrder";
import { OrderInfo } from "../../../components/order";
import { ArtPost } from "../../art/[slug]";
import { NEW_SLUG, USER_AUTH_URL } from "../../../config";

export const UpdateArt = ({
  art: initialArt,
  slug: initialSlug,
}: {
  art: ArtData | null;
  slug: string;
}) => {
  const setContent = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();

  useEffect(() => {
    if (!permissions?.data?.loggedIn) {
      navigate(
        USER_AUTH_URL("login", { flow: "resume", to: location.pathname }),
      );
      return;
    }
    if (!permissions?.data?.user?.admin) {
      navigate("/");
      return;
    }
  }, [permissions, navigate, location.pathname]);

  const slug = initialSlug;
  const artRef = useRef(
    initialArt
      ? initialArt
      : ({
          type: SITE_DATA_PARTITIONS.ART,
          slug: "",
          title: "",
          description: "",
          dimensions: {
            w: 0,
            d: 0,
            h: 0,
            unit: "in",
          },
          images: [],
          mediums: [],
          author: "",
          isPublic: false,
          isInProgress: false,
          price: undefined,
          currency: "usd",
          isHidden: true,
        } as ArtData),
  );

  const [currArt, setCurrArt] = useState(artRef.current);
  const [orderRequest] = useOrder({ slug: currArt?.orderSlug });
  const [loading, setLoading] = useState(false);
  const uploadedImages = useRef<{ originalFileName: string; size: string }[]>(
    [],
  );
  const [imageErrors, setImageErrors] = useState<{ message: string }[]>([]);
  const [imagesLoading, setImagesLoading] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  useEffect(() => {
    const _run = async () => {
      try {
        if (currArt?.images && currArt.images.length) {
          for (let i = 0; i < currArt.images.length; i++) {
            const image = currArt.images[i];
            if (
              image.path ||
              uploadedImages.current.find((i) => isSameFile(i, image))
            )
              continue;
            uploadedImages.current.push(image);
            setImagesLoading((names) => [...names, image.originalFileName]);
            try {
              const uploadResponse = await ImageApi.create(image);
              if (uploadResponse.status !== 200) {
                throw Error(uploadResponse.result);
              }
              setCurrArt((currArt) => ({
                ...currArt,
                images: currArt.images?.map((i) => {
                  return isSameFile(i, image)
                    ? { ...i, path: uploadResponse.storedName, data: undefined }
                    : { ...i };
                }),
              }));
            } catch (e: any) {
              setImageErrors((errors) => [...errors, e]);
            } finally {
              setImagesLoading((names) =>
                names.filter((name) => name !== image.originalFileName),
              );
            }
          }
        }
      } catch (e) {
        console.log(e);
      }
    };

    _run();
  }, [currArt?.images]);
  const inSync = Object.keys({ ...currArt, ...artRef.current }).every(
    (key) =>
      JSON.stringify(currArt[key as keyof typeof currArt]) ===
      JSON.stringify(artRef.current[key as keyof typeof currArt]),
  );
  const updateArt = useCallback(
    (updatedArt: ArtData & ArtKeyComponents) => {
      setLoading(true);

      const manipulation =
        slug === NEW_SLUG ? Api.create<ArtData> : Api.update<ArtData>;
      manipulation({ ...updatedArt })
        .then((res) => {
          if (res.success) {
            setLoading(false);
            artRef.current = { ...artRef.current, ...updatedArt };
            if (slug === NEW_SLUG) {
              navigate("./" + updatedArt.slug);
            }
          } else {
            throw new Error(res.error);
          }
        })
        .catch((e) => {
          setLoading(false);
          setErrors((errors) => [...errors, e.message]);
        });
    },
    [slug, setLoading],
  );

  const deleteArt = useCallback(() => {
    setLoading(true);
    const manipulation = Api.delete;
    manipulation({ type: currArt.type, slug: currArt.slug })
      .then((res) => {
        setLoading(false);
        setTimeout(() => navigate("/admin/art"), 200);
      })
      .catch((e) => setLoading(false));
  }, [setLoading, currArt.slug, currArt.type]);

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement & HTMLSelectElement>,
  ) => {
    const { value, name } = evt.target;
    const [root, subKey] = name.split(".");
    setCurrArt((art) => ({
      ...art,
      [root]:
        root === "slug"
          ? value.replace(/\s/, "").toLowerCase()
          : root === "dimensions"
            ? { ...art[root], [subKey]: value }
            : root === "price"
              ? Math.round(parseFloat(value) * 100) / 100
              : value,
    }));
  };
  const height = 200;
  // const hoverHeight = 250;
  return (
    <Layout headerStyle="sm">
      <div className="min-h-[20px] z-[1000] sticky top-0 flex justify-between gap-2 bg-main p-2 px-7 shadow-md">
        <Link
          to="/admin/art"
          className={BUTTON_TYPE.small + BUTTON_COLORS.secondary + ""}
        >
          Back to the art list
        </Link>

        <h1 className="text-center self-center">
          {slug === NEW_SLUG
            ? "CREATE SOME ART"
            : 'Editing the art titled "' + currArt.title + '"'}
        </h1>

        {loading ? (
          <div className={BUTTON_TYPE.small + BUTTON_COLORS.warning}>
            Loading
          </div>
        ) : inSync && slug !== NEW_SLUG ? (
          <div className={BUTTON_TYPE.small + BUTTON_COLORS.success}>
            In sync
          </div>
        ) : (
          <button
            className={BUTTON_TYPE.small + BUTTON_COLORS.warning}
            onClick={() => updateArt(currArt)}
          >
            {slug === NEW_SLUG ? "Create" : "Sync now"}
          </button>
        )}
      </div>
      <div className="p-4">
        <div className=" flex flex-col 2xl:flex-row flex-wrap">
          <div className=" flex-1   border-2 border-primary">
            <h2 className="p-2">Preview</h2>
            <ArtPost art={currArt} />
          </div>
          <div className="px-10 pb-16 flex-1 ">
            <div className="min-h-[20px] z-[1000] fixed top-4 right-4">
              {errors.map((e, idx) => (
                <div
                  className="border-2 border-textWarning bg-white p-2 rounded-xl my-2 flex justify-around"
                  key={e}
                >
                  <div className="flex-1 basis-9/12 m-1">{e}</div>
                  <div className="flex-1 basis-1/12 align-center">
                    <Button
                      btnType="nav"
                      btnColor="warning"
                      className="w-[64px] h-[64px] text-3xl !p-0"
                      onClick={() => {
                        setErrors((errors) =>
                          errors.filter((_e, i) => idx != i),
                        );
                      }}
                    >
                      🗑️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Input
              label="Slug"
              name="slug"
              disabled={slug !== NEW_SLUG || loading}
              placeholder="Slug"
              value={currArt.slug}
              onChange={handleChange}
            />
            <Input
              label="Title"
              name="title"
              disabled={loading}
              placeholder="Title"
              value={currArt.title}
              onChange={handleChange}
            />
            {artRef.current.slug ? (
              <div className="my-3">
                <label htmlFor="images" className="custom-file-upload p-4">
                  Upload images
                </label>
                <input
                  type="file"
                  multiple
                  id="images"
                  name="images"
                  accept="image/jpeg,image/png"
                  className=""
                  disabled={loading}
                  onChange={(evt) => {
                    const { files } = evt.target;
                    if (!files) {
                      alert("No files in upload");
                      return;
                    } else {
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const reader = new FileReader();
                        reader.onload = function (e) {
                          const data = e.target?.result;
                          if (data) {
                            const newImage = {
                              name:
                                currArt.slug +
                                "-image." +
                                file.name.split(".").pop(),
                              originalFileName: file.name,
                              size: file.size,
                              data,
                            };
                            uploadedImages.current =
                              uploadedImages.current.filter(
                                (i) => !isSameFile(i, newImage),
                              );
                            setCurrArt((art) => ({
                              ...art,
                              images: [
                                ...(art?.images?.filter(
                                  (i) => !isSameFile(i, newImage),
                                ) ?? []),
                                newImage,
                              ],
                            }));
                          }
                        };
                        reader.onerror = function (e) {
                          console.log("Error : " + e.type);
                        };
                        reader.readAsDataURL(file);
                      }
                    }
                  }}
                />
                <div className="text-secondary">
                  {imageErrors.map((e, i) =>
                    e.message ? (
                      <div key={e.message}>{e.message}</div>
                    ) : (
                      <div key={i}>Unkown Image Error</div>
                    ),
                  )}
                </div>

                <div className="text-secondary">
                  {imagesLoading.map((name, i) => (
                    <div key={name}>
                      <Loader />
                      {name}
                    </div>
                  ))}
                </div>
                <div className="text-accent my-3 flex flex-wrap">
                  {currArt.images?.map((image, idx, arr) => (
                    <div
                      key={image.originalFileName}
                      className="flex-[0_2_auto] m-2"
                    >
                      {/* <Input
                      name={"ref" + image.name}
                      label="Image Ref"
                      className="my-4 p-4 w-full text-primary"
                      disabled={loading}
                      placeholder="Image ref"
                      value={image.ref ?? image.name}
                      onChange={(evt) => {
                        const { value } = evt.target;
                        setCurrArt((art) => ({
                          ...art,
                          images: art.images?.map((i) => {
                            return isSameFile(i, image)
                              ? { ...i, ref: value || image.name }
                              : { ...i };
                          }),
                        }));
                      }}
                    /> */}

                      {image.path ? (
                        <>
                          <ImagePreview
                            transition={300}
                            absolute
                            alt={image.originalFileName}
                            image={image}
                            rounded
                            size={{
                              height: height,
                              top: "50%",
                              left: "-50%",
                              transformX: "50%",
                              transformY: "-50%",
                              z: 1,
                            }}
                            // hoverSize={{
                            //   height: hoverHeight,
                            //   z: 100,
                            //   top: "50%",
                            //   left: "-100%",
                            //   transformX:
                            //     (height / hoverHeight -
                            //       (hoverHeight - height) / hoverHeight / 2) *
                            //       100 +
                            //     "%",
                            //   transformY:
                            //     // (-((hoverHeight - height) / hoverHeight / 2) * 50) - 50 +
                            //     "-50%",
                            // }}
                            actionSize="3rem"
                            actionIcon={<div className="text-2xl">🗑️</div>}
                            actionCorner="bottomLeft"
                            actionTitle="Delete image"
                            actionColor="warning"
                            onAction={() => {
                              setCurrArt((art) => ({
                                ...art,
                                images: [
                                  ...(art?.images?.filter(
                                    (i) => !isSameFile(i, image),
                                  ) ?? []),
                                ],
                              }));
                            }}
                            onClick={() =>
                              setContent({
                                component: (
                                  <div className="h-fit p-6 ">
                                    <ImagePreview
                                      alt={image.originalFileName}
                                      image={image}
                                      size={{
                                        maxWidth: "90vw",
                                        maxHeight: "90vh",
                                        z: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    />
                                  </div>
                                ),
                                justify: "center",
                              })
                            }
                          />
                          <div className="pt-1 ">
                            <div className="-mx-1">
                              {idx > 0 ? (
                                <Button
                                  btnType="xs"
                                  btnColor="nav"
                                  onClick={() =>
                                    setCurrArt((art) => {
                                      const newImages = [
                                        ...(art?.images ?? []),
                                      ];
                                      const image = newImages.splice(idx, 1)[0];
                                      if (image) {
                                        newImages.splice(idx - 1, 0, image);
                                      }
                                      return {
                                        ...art,
                                        images: newImages,
                                      };
                                    })
                                  }
                                >
                                  Move up
                                </Button>
                              ) : null}

                              {idx < arr.length - 1 ? (
                                <Button
                                  btnType="xs"
                                  btnColor="nav"
                                  onClick={() =>
                                    setCurrArt((art) => {
                                      const newImages = [
                                        ...(art?.images ?? []),
                                      ];
                                      const image = newImages.splice(idx, 1)[0];
                                      if (image) {
                                        newImages.splice(idx + 1, 0, image);
                                      }
                                      return {
                                        ...art,
                                        images: newImages,
                                      };
                                    })
                                  }
                                >
                                  Move down
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="m-auto w-[40px]" style={{ height }}>
                          <Loader />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="my-3">
                Pick a slug and create the art to upload an image
              </div>
            )}
            <div className="my-3">
              <label htmlFor="mediums" className="block my-1">
                Art medium (select all that apply)
              </label>
              {Object.values(MEDIUM_TYPE).map(({ key, label }) => (
                <div className="inline" key={key}>
                  <label className="cursor-pointer" htmlFor={key}>
                    <input
                      name={key}
                      id={key}
                      type="checkbox"
                      className=""
                      disabled={loading}
                      checked={currArt.mediums?.includes(key) ? true : false}
                      onChange={(evt) => {
                        const { checked } = evt.target;
                        setCurrArt((art) => ({
                          ...art,
                          mediums: checked
                            ? [...(art.mediums ?? []), key]
                            : (art.mediums?.filter(
                                (medium) => key !== medium,
                              ) ?? []),
                        }));
                      }}
                    />
                    <span className="m-2 ">{label}</span>
                  </label>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap flex-1 my-1 xl:my-3">
              <div className="flex-1 min-w-[70px] ">
                <Input
                  label="Height"
                  name="dimensions.h"
                  type="number"
                  disabled={loading}
                  placeholder="Height"
                  className="!my-0"
                  value={currArt.dimensions?.h}
                  onChange={handleChange}
                />
              </div>
              <div className="flex-1 min-w-[70px]">
                <Input
                  label="Width"
                  name="dimensions.w"
                  type="number"
                  className="!my-0"
                  placeholder="Width"
                  value={currArt.dimensions?.w}
                  onChange={handleChange}
                />
              </div>
              <div className="flex-1 min-w-[70px]">
                <Input
                  label="Depth"
                  name="dimensions.d"
                  type="number"
                  className="!my-0"
                  disabled={loading}
                  placeholder="Depth"
                  value={currArt.dimensions?.d}
                  onChange={handleChange}
                />
              </div>
              <div className="flex-1 min-w-[100px]">
                <Input
                  label="Unit"
                  name="dimensions.unit"
                  className="!my-0"
                  disabled={loading}
                  placeholder="Unit"
                  value={currArt.dimensions?.unit}
                  onChange={handleChange}
                  inputType="select"
                >
                  <option value="">Select a unit</option>
                  <option value="in">Inch</option>
                </Input>
              </div>
            </div>
            <Input
              type="textArea"
              label="Description"
              name="description"
              disabled={loading}
              placeholder="Description"
              value={currArt.description}
              onChange={handleChange}
            />
            <Input
              name="author"
              placeholder="Author"
              disabled={loading}
              value={currArt.author}
              onChange={handleChange}
              inputType="select"
              label="Artist"
            >
              <option value="">Select</option>
              <option value="Zoë Williams">Z-Word</option>
              <option value="Sam Crewe-Sullam">SAMMA</option>
            </Input>

            <div className="flex flex-wrap flex-1">
              <div className="flex-[4_1_0%] min-w-[200px]">
                <Input
                  type="number"
                  label="Price"
                  step=".01"
                  name="price"
                  disabled={loading}
                  placeholder="10,000.00"
                  value={currArt.price}
                  onChange={handleChange}
                />
              </div>

              <div className="flex-1 min-w-[100px]">
                <Input
                  label="Currency"
                  name="currency"
                  disabled={loading}
                  placeholder="Please select"
                  value={currArt.currency}
                  onChange={handleChange}
                  inputType="select"
                >
                  <option value="">Select a unit</option>
                  {Object.values(CURRENCY_TYPE).map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
            <div>
              <input
                name="isPublic"
                id="isPublic"
                type="checkbox"
                className=""
                disabled={loading}
                checked={currArt.isPublic ? true : false}
                onChange={(evt) => {
                  const { checked, name } = evt.target;
                  setCurrArt((blog) => ({ ...blog, [name]: checked }));
                }}
              />
              <label htmlFor="isPublic" className="m-2 cursor-pointer">
                Is Public
              </label>
            </div>
            <div>
              <input
                name="isInProgress"
                id="isInProgress"
                type="checkbox"
                className=""
                disabled={loading}
                checked={currArt.isInProgress ? true : false}
                onChange={(evt) => {
                  const { checked, name } = evt.target;
                  setCurrArt((blog) => ({ ...blog, [name]: checked }));
                }}
              />
              <label htmlFor="isInProgress" className="m-2 cursor-pointer">
                Is in progress
              </label>
            </div>

            <Input
              label="orderSlug"
              name="orderSlug"
              disabled={loading}
              showLockDown
              placeholder="Order slug"
              value={currArt.orderSlug}
              onChange={handleChange}
            />

            {slug !== NEW_SLUG ? (
              <Button btnColor="warning" btnType="small" onClick={deleteArt}>
                DELETE
              </Button>
            ) : null}
          </div>
        </div>
        {orderRequest?.status ? (
          <OrderInfo
            {...orderRequest}
            showViewShipmentBtn
            showViewOrderBtn
            showCreateShipmentBtn
            showShipmentItems
            showItems
            admin={true}
          />
        ) : null}
      </div>
    </Layout>
  );
};

// Wrapper component that fetches data and renders UpdateArt
export default function AdminArtEdit() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [art, setArt] = useState<ArtData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArt() {
      if (!slug) {
        navigate("/admin/art");
        return;
      }

      // Handle NEW_SLUG case - no need to fetch
      if (slug === NEW_SLUG) {
        setArt(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const { success, data } = await Api.get<ArtData>({
          type: SITE_DATA_PARTITIONS.ART,
          slug,
        });

        if (success && data) {
          setArt(data);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching art:", err);
        setLoading(false);
      }
    }

    fetchArt();
  }, [slug, navigate]);

  if (loading) {
    return null;
  }

  return <UpdateArt art={art} slug={slug!} />;
}
